import {
  Breadcrumbs,
  Button,
  ButtonProps,
  FormControl,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import { Field } from "formik";
import { TextField } from "formik-mui";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";
import React, { ReactNode, useState } from "react";
import RenameIcon from "@mui/icons-material/Edit";

interface RigDetailsHeaderBreadcrumbProps {
  text: string;
  onClick: React.MouseEventHandler;
  Component?: JSX.Element;
}

interface RigDetailsHeaderProps {
  breadcrumbs: RigDetailsHeaderBreadcrumbProps[];
  headline: string;
  editHeadlineFormValuePath?: string;
  actionButtons: (Pick<
    ButtonProps,
    "color" | "startIcon" | "onClick" | "disabled"
  > & {
    text: string;
    disabledTooltip?: ReactNode;
  })[];
  readOnly: boolean;
}

export const RigDetailsHeader: React.FC<RigDetailsHeaderProps> = ({
  breadcrumbs,
  headline,
  editHeadlineFormValuePath,
  actionButtons,
  readOnly,
}) => {
  const { t } = useTranslation();
  const [isEditingHeadline, setIsEditingHeadline] = useState(false);

  let headlineComponent: JSX.Element | undefined;
  let editButtonComponent: JSX.Element | undefined;

  if (isEditingHeadline) {
    headlineComponent = (
      <FormControl>
        <Field component={TextField} name={editHeadlineFormValuePath} />
      </FormControl>
    );
  } else if (headline === "") {
    headlineComponent = (
      <Typography className="text-4xl text-gray-400 italic">
        {t("models.equipment.noName")}
      </Typography>
    );
  } else {
    headlineComponent = (
      <Typography className="text-4xl">{headline}</Typography>
    );
  }

  if (isEditingHeadline) {
    editButtonComponent = (
      <Button
        variant="contained"
        startIcon={<CheckIcon />}
        color="success"
        className="h-min self-center rounded-lg"
        onClick={() => setIsEditingHeadline(false)}
      >
        {t("utils.actions.apply")}
      </Button>
    );
  } else if (editHeadlineFormValuePath) {
    editButtonComponent = (
      <Button
        variant="contained"
        startIcon={<RenameIcon />}
        color="secondary"
        className="h-min self-center rounded-lg"
        onClick={() => setIsEditingHeadline(true)}
        disabled={readOnly}
      >
        {t("utils.actions.rename")}
      </Button>
    );
  }

  return (
    <div className="flex flex-row gap-4">
      <div>
        <Breadcrumbs aria-label="breadcrumb" className="text-lg text-gray-400">
          {breadcrumbs.map((breadcrumb, index) => {
            return (
              <Link
                className="cursor-pointer"
                onClick={breadcrumb.onClick}
                key={index}
                color="inherit"
                underline="hover"
              >
                {breadcrumb.text}
              </Link>
            );
          })}
        </Breadcrumbs>
        {headlineComponent}
      </div>
      {editButtonComponent}
      <div className="flex-1" />
      {actionButtons.map((button, index) =>
        button.disabled && button.disabledTooltip ? (
          <Tooltip key={index} title={button.disabledTooltip} arrow={true}>
            {/* MUI won't render a tooltip on a disabled button, so wrap it in a span */}
            <span className="h-min self-center">
              <Button
                variant="contained"
                color={button.color}
                startIcon={button.startIcon}
                className="rounded-lg disabled:bg-zinc-500 disabled:text-lighten-500"
                onClick={button.onClick}
                disabled={true}
              >
                {button.text}
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            key={index}
            variant="contained"
            color={button.color}
            startIcon={button.startIcon}
            className="h-min self-center rounded-lg"
            onClick={button.onClick}
            disabled={button.disabled}
          >
            {button.text}
          </Button>
        )
      )}
    </div>
  );
};
